import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, HelpCircle } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Breadcrumb } from '../components/Breadcrumb';
import { BreadcrumbSchema } from '../components/SchemaOrg';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Helmet } from 'react-helmet-async';

const faqs = [
  {
    question: 'Quels sont vos délais d\'intervention en urgence ?',
    answer: 'Pour toute urgence plomberie (fuite d\'eau, canalisation bouchée, dégât des eaux), nous intervenons en 30 minutes en moyenne à Paris 17e et dans les arrondissements et communes limitrophes. Notre service d\'urgence est disponible 24h/24 et 7j/7, week-ends et jours fériés compris.'
  },
  {
    question: 'Le devis est-il vraiment gratuit ?',
    answer: 'Oui, le devis est 100% gratuit et sans engagement. Avant toute intervention, notre plombier établit un diagnostic précis et vous remet un devis détaillé. Aucuns travaux ne sont commencés sans votre accord écrit sur le prix. Pas de surprise sur la facture.'
  },
  {
    question: 'Quelles zones couvrez-vous ?',
    answer: 'Nous sommes basés au 18 rue Legendre, 75017 Paris et intervenons principalement à Paris 17e, ainsi que dans les arrondissements voisins (8e, 9e, 16e, 18e) et les communes limitrophes : Levallois-Perret, Clichy, Neuilly-sur-Seine et l\'ensemble de la proche banlieue.'
  },
  {
    question: 'Combien coûte une intervention de plomberie ?',
    answer: 'Le prix dépend de la nature de l\'intervention : un débouchage simple, une réparation de fuite ou un remplacement de chauffe-eau n\'ont pas le même coût. C\'est pourquoi nous établissons systématiquement un devis gratuit avant intervention. Nos tarifs sont transparents, sans frais cachés, et nous appliquons la TVA réduite à 10% quand votre logement y est éligible (plus de 2 ans).'
  },
  {
    question: 'Que faire en cas de fuite d\'eau avant votre arrivée ?',
    answer: 'Coupez immédiatement l\'eau au robinet d\'arrêt général (généralement près du compteur). Si l\'eau menace des prises électriques, coupez également le disjoncteur. Épongez, placez des récipients sous la fuite et protégez vos meubles. Prévenez vos voisins si l\'eau s\'infiltre chez eux, puis appelez-nous au 09 70 70 58 63.'
  },
  {
    question: 'Vos interventions sont-elles garanties ?',
    answer: 'Oui. Tous nos travaux sont couverts par notre assurance responsabilité civile professionnelle et notre garantie décennale. Les pièces installées bénéficient de la garantie fabricant, et notre main d\'œuvre est garantie. En cas de problème après notre passage, nous revenons sans frais.'
  },
  {
    question: 'Quels moyens de paiement acceptez-vous ?',
    answer: 'Nous acceptons les paiements par carte bancaire, virement, chèque et espèces (dans la limite légale). Une facture détaillée vous est systématiquement remise après chaque intervention, utile notamment pour votre assurance en cas de dégât des eaux.'
  },
  {
    question: 'Intervenez-vous la nuit, le week-end et les jours fériés ?',
    answer: 'Oui, notre service d\'urgence fonctionne 24h/24 et 7j/7, y compris la nuit, les week-ends et les jours fériés. Une fuite ou un dégât des eaux n\'attend pas : appelez-nous à toute heure au 09 70 70 58 63.'
  },
  {
    question: 'Pouvez-vous établir un constat pour mon assurance ?',
    answer: 'Absolument. Après une intervention liée à un dégât des eaux, nous vous fournissons une facture détaillée et les éléments nécessaires à votre déclaration de sinistre (origine de la fuite, nature des réparations). Pensez à déclarer le sinistre à votre assurance sous 5 jours ouvrés.'
  },
  {
    question: 'Remplacez-vous les chauffe-eau ?',
    answer: 'Oui, nous assurons le dépannage, l\'entretien (détartrage) et le remplacement complet de chauffe-eau électriques, à gaz et thermodynamiques. Nous vous conseillons sur le modèle et la capacité adaptés à votre logement, et l\'ancien appareil est évacué par nos soins.'
  }
];

const FAQ = () => {
  const breadcrumbItems = [
    { name: 'FAQ', url: '/faq' }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEO
        title="FAQ Plomberie - Questions Fréquentes | NHM Plomberie Paris 17e"
        description="Toutes les réponses à vos questions : délais d'intervention, devis gratuit, tarifs, garanties, urgences 24/7. NHM Plomberie, votre plombier à Paris 17e."
        keywords="FAQ plombier, questions plomberie, tarif plombier Paris, délai intervention plombier, devis plomberie gratuit, garantie plomberie"
        canonical="https://nhm-plomberie.fr/faq"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <BreadcrumbSchema items={[{ name: 'Accueil', url: 'https://nhm-plomberie.fr/' }, ...breadcrumbItems.map(item => ({ name: item.name, url: `https://nhm-plomberie.fr${item.url}` }))]} />

      <Breadcrumb items={breadcrumbItems} />

      <section className="bg-blue-900 text-white py-16">
        <div className="container mx-auto px-4">
          <HelpCircle className="w-14 h-14 mb-4 text-blue-200" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Questions Fréquentes</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Délais, tarifs, garanties, urgences : toutes les réponses à vos questions sur nos services de plomberie à Paris.
          </p>
        </div>
      </section>

      <section className="py-16" data-testid="faq-section">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="border-2 rounded-lg px-6 data-[state=open]:border-blue-600"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left text-lg font-semibold hover:text-blue-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 text-base leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">Vous ne trouvez pas votre réponse ?</h2>
          <p className="text-lg text-gray-600 mb-8">Contactez-nous directement, nous vous répondons rapidement.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" data-testid="faq-call-btn">
              <a href="tel:0970705863" className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                09 70 70 58 63
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" data-testid="faq-contact-btn">
              <Link to="/contact">Nous écrire</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQ;
